<script>
import LandingBadge from '@rei/landing-badge';
import {
  CdrLink,
  CdrImg,
  CdrRating,
} from '@rei/cedar';
import LandingPriceComponent from './FilmstripPriceComponent.vue';

export default {
  name: 'ProductTile',
  components: {
    LandingPriceComponent,
    CdrLink,
    CdrImg,
    LandingBadge,
    CdrRating,
  },
  props: {
    brand: { type: String, default: '', required: true },
    href: { type: String, default: '', required: true },
    price: { type: Object, default: () => {}, required: true },
    title: { type: String, default: '', required: true },
    media: { type: Object, default: () => {}, required: true },
    badge: { type: Object, default: () => {}, required: false },
    rating: { type: Object, default: () => {}, required: false },
    lastSlide: { type: Boolean, required: false, default: false },
  },
  computed: {
    averageRating() {
      return this.rating?.averageRating || 0;
    },
    reviewCount() {
      return this.rating?.count || 0;
    },
  },
};
</script>
<template>
  <div
    class="landing-product-tile"
    :class="{ 'last-slide': lastSlide }"
  >
    <div
      class="landing-product-tile__img-wrap"
    >
      <cdr-img
        v-if="media && media.uri"
        :src="media.uri"
        :alt="title"
        class="landing-product-tile__img"
      />

      <landing-badge
        v-if="badge && badge.text"
        class="landing-product-tile__badge"
        v-bind="badge"
      />
    </div>
    <div
      class="landing-product-tile__content"
    >
      <p
        class="landing-product-tile__brand"
        v-html="brand"
      />

      <cdr-link
        :href="href"
        modifier="standalone"
        class="landing-product-tile__link"
      >
        <h3
          class="landing-product-tile__title"
          tag="h3"
          v-html="title"
        />
      </cdr-link>

      <landing-price-component
        class="landing-product-tile__price"
        v-bind="price"
      />

      <cdr-rating
        :rating="averageRating"
        :count="reviewCount"
        class="landing-product-tile__rating"
        size="small"
        compact
      />
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';
@import "../../../../style/filmstripMixins.scss";

.landing-product-tile {
  position: relative;
  gap: 16px;

  @include cdr-sm-mq-up {
    margin-right: 16px;
  }

  @include cdr-lg-mq-up {
    margin-right: 24px;
  }

  &.last-slide {
    margin-right: 0;
  }

  &__img-wrap {
    background-color: $cdr-color-background-secondary;
    margin-bottom: $cdr-space-three-quarter-x;
    padding: $cdr-space-one-x;
    border-radius: 4px;
    position: relative;
    aspect-ratio: 1;
    display: flex;
    width: 202px;
    max-width: 324px;
    min-width: 202px;

    @include cdr-sm-mq-up {
      width: calc((100vw - 100px) / 3.4);
    }
  }

  &__badge {
    left: 0;
    position: absolute;
    bottom: $cdr-space-one-and-a-half-x;
  }

  &__img {
    object-fit: contain;
    aspect-ratio: 1;
    object-fit: contain;
    display: block;
    margin: 0 auto;
    mix-blend-mode: darken;
  }

  &__link {
    @include link-tile-cover;
    // Add an extra z-index to overlay the cedar rating component.
    &::before {
      z-index: 2;
    }
  }

  &__brand,
  &__title {
    text-decoration: inherit;
    margin: 0;

    &,
    &:hover,
    &:active,
    &:focus,
    &:visited {
      color: $cdr-color-text-primary;
    }
  }

  &__brand {
    @include cdr-text-utility-sans-100;
    color: $cdr-color-text-secondary;
    margin-bottom: 4px;
    @include cdr-md-mq-up {
      @include cdr-text-utility-sans-200;
    }
  }

  &__title {
    @include cdr-text-heading-sans-300;
    margin-bottom: 4px;
    @include cdr-md-mq-up {
      @include cdr-text-heading-sans-400;
    }
  }

  &__price {
    @include cdr-text-heading-sans-200;
    margin-bottom: 4px;
    @include cdr-md-mq-up {
      @include cdr-text-heading-sans-300;
    }
  }
}
</style>
